import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  Cheque, ChequeBook, ChequeBounceDTO, ChequeDirection, ChequeLayout, ChequePage, ChequeStatus,
  IssuedChequeCreateDTO, ReceivedChequeCreateDTO, BankDueItem,
} from '../interfaces/bank/cheque.interface';
import { WithholdingLineInput } from '../interfaces/procurement/withholding.interface';

export interface ChequeFilters {
  direction?: ChequeDirection;
  status?: ChequeStatus;
  bankAccountId?: string;
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  size?: number;
}

export const ChequeService = {
  list: async (institutionId: string, filters: ChequeFilters = {}): Promise<ChequePage> => {
    const params: Record<string, string | number> = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== '' && v !== null) params[k] = v as string | number;
    });
    return (await axios.get(ENDPOINTS.CHEQUE.LIST(institutionId), { params })).data;
  },
  get: async (id: string): Promise<Cheque> => (await axios.get(ENDPOINTS.CHEQUE.GET(id))).data,
  registerReceived: async (dto: ReceivedChequeCreateDTO): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.REGISTER_RECEIVED, dto)).data,
  registerIssued: async (dto: IssuedChequeCreateDTO): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.REGISTER_ISSUED, dto)).data,
  deposit: async (id: string, bankAccountId: string, depositDate?: string): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.DEPOSIT(id), { bankAccountId, depositDate })).data,
  clear: async (id: string, clearedDate?: string): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.CLEAR(id), { clearedDate })).data,
  bounce: async (id: string, dto: ChequeBounceDTO): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.BOUNCE(id), dto)).data,
  markReturned: async (id: string, reason?: string): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.RETURN(id), { reason })).data,
  release: async (id: string, releaseDate?: string, supplierBillId?: string, withholdings?: WithholdingLineInput[]): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.RELEASE(id), { releaseDate, supplierBillId, withholdings })).data, // B4-0 (0-3)
  stop: async (id: string, reason?: string): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.STOP(id), { reason })).data,
  cancel: async (id: string, reason?: string): Promise<Cheque> =>
    (await axios.post(ENDPOINTS.CHEQUE.CANCEL(id), { reason })).data,
  print: async (id: string, layoutId?: string): Promise<Blob> =>
    (await axios.get(ENDPOINTS.CHEQUE.PRINT(id, layoutId), { responseType: 'blob' })).data,
  dueItems: async (institutionId: string, from: string, to: string): Promise<BankDueItem[]> =>
    (await axios.get(ENDPOINTS.BANK_DUE_ITEMS.LIST(institutionId, from, to))).data,
};

export const ChequeBookService = {
  list: async (institutionId: string): Promise<ChequeBook[]> =>
    (await axios.get(ENDPOINTS.CHEQUE_BOOK.LIST(institutionId))).data,
  create: async (dto: ChequeBook): Promise<ChequeBook> => (await axios.post(ENDPOINTS.CHEQUE_BOOK.BASE, dto)).data,
  update: async (id: string, dto: ChequeBook): Promise<ChequeBook> =>
    (await axios.put(ENDPOINTS.CHEQUE_BOOK.ONE(id), dto)).data,
  remove: async (id: string): Promise<void> => {
    await axios.delete(ENDPOINTS.CHEQUE_BOOK.ONE(id));
  },
};

export const ChequeLayoutService = {
  list: async (institutionId: string): Promise<ChequeLayout[]> =>
    (await axios.get(ENDPOINTS.CHEQUE_LAYOUT.LIST(institutionId))).data,
  create: async (dto: ChequeLayout): Promise<ChequeLayout> =>
    (await axios.post(ENDPOINTS.CHEQUE_LAYOUT.BASE, dto)).data,
  update: async (id: string, dto: ChequeLayout): Promise<ChequeLayout> =>
    (await axios.put(ENDPOINTS.CHEQUE_LAYOUT.ONE(id), dto)).data,
  remove: async (id: string): Promise<void> => {
    await axios.delete(ENDPOINTS.CHEQUE_LAYOUT.ONE(id));
  },
  loadDefaults: async (institutionId: string): Promise<ChequeLayout[]> =>
    (await axios.post(ENDPOINTS.CHEQUE_LAYOUT.LOAD_DEFAULTS(institutionId))).data,
};
